var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import mergeRefs from 'merge-refs';
import { forwardRef, useEffect, useRef, useState, } from 'react';
import { ButtonFunction } from '@snack-uikit/button';
import { MinusSVG, PlusSVG } from '@snack-uikit/icons';
import { InputPrivate, SIZE } from '@snack-uikit/input-private';
import { useLocale } from '@snack-uikit/locale';
import { Tooltip } from '@snack-uikit/tooltip';
import { extractSupportProps } from '@snack-uikit/utils';
import { CONTAINER_VARIANT, VALIDATION_STATE } from '../../constants';
import { FieldContainerPrivate } from '../../helperComponents';
import { usePostfix, usePrefix, useValueControl } from '../../hooks';
import { getValidationState } from '../../utils/getValidationState';
import { FieldDecorator } from '../FieldDecorator';
import styles from './styles.module.css';
const TOOLTIP_TIMEOUT = 2000;
const getDefaultValue = (min, max) => {
    if (min > 0) {
        return min;
    }
    if (max < 0) {
        return max;
    }
    return 0;
};
const SymbolWidth = {
    s: 8,
    m: 9,
    l: 10,
};
export const FieldStepper = forwardRef((_a, ref) => {
    var { id, name, value: valueProp, min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY, step = 1, disabled = false, readonly = false, allowMoreThanLimits = true, onChange: onChangeProp, onFocus, onBlur, className, label, labelTooltip, labelTooltipPlacement, required = false, caption, hint, showHintIcon, size = SIZE.S, validationState = VALIDATION_STATE.Default, error, prefix, postfix } = _a, rest = __rest(_a, ["id", "name", "value", "min", "max", "step", "disabled", "readonly", "allowMoreThanLimits", "onChange", "onFocus", "onBlur", "className", "label", "labelTooltip", "labelTooltipPlacement", "required", "caption", "hint", "showHintIcon", "size", "validationState", "error", "prefix", "postfix"]);
    const { t } = useLocale('Fields');
    const [value = 0, setValue] = useValueControl({
        value: valueProp,
        defaultValue: getDefaultValue(min, max),
        onChange: onChangeProp,
    });
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltip, setTooltip] = useState('');
    const timerRef = useRef();
    const inputRef = useRef(null);
    const isMinusButtonDisabled = (typeof min === 'number' && value <= min) || readonly || disabled;
    const isPlusButtonDisabled = (typeof max === 'number' && value >= max) || readonly || disabled;
    const prefixSettings = usePrefix({ prefix, disabled });
    const postfixSettings = usePostfix({ postfix, disabled });
    const fieldValidationState = getValidationState({ validationState, error });
    const adjustValue = (value) => {
        setValue(value);
        setTooltipOpen(true);
        timerRef.current = setTimeout(() => {
            setTooltipOpen(false);
            setTooltip('');
        }, TOOLTIP_TIMEOUT);
    };
    useEffect(() => () => {
        clearTimeout(timerRef.current);
    }, []);
    const handleInputBlur = (event) => {
        if (!allowMoreThanLimits) {
            if (max !== undefined && value > max) {
                setTooltip(`${t('limitTooltip.max')}${max}`);
                adjustValue(max);
            }
            if (min !== undefined && value < min) {
                setTooltip(`${t('limitTooltip.min')}${min}`);
                adjustValue(min);
            }
        }
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    };
    const handleInputFocus = (event) => {
        clearTimeout(timerRef.current);
        setTooltipOpen(false);
        setTooltip('');
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
    };
    const handleInputChange = (value, event) => setValue(Number(value), event);
    const handleInputKeyDown = () => {
        if (readonly || disabled) {
            return;
        }
    };
    const handleMinusButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setValue(Math.min(Math.max(min, value - step), max));
    };
    const handlePlusButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setValue(Math.max(Math.min(max, value + step), min));
    };
    const handleMinusButtonKeyDown = () => {
        if (readonly || disabled) {
            return;
        }
    };
    const handlePlusButtonKeyDown = () => {
        if (readonly || disabled) {
            return;
        }
    };
    return (_jsx(FieldDecorator, Object.assign({ className: className, label: label, labelTooltip: labelTooltip, labelTooltipPlacement: labelTooltipPlacement, labelFor: id, required: required, caption: caption, hint: hint, disabled: disabled, readonly: readonly, showHintIcon: showHintIcon, size: size, validationState: fieldValidationState, error: error }, extractSupportProps(rest), { children: _jsx(Tooltip, { tip: tooltip, open: allowMoreThanLimits ? false : tooltipOpen, "data-test-id": 'field-stepper__limit-tooltip', triggerClassName: styles.trigger, children: _jsx(FieldContainerPrivate, { size: size, validationState: fieldValidationState, disabled: disabled, readonly: readonly, variant: CONTAINER_VARIANT.SingleLine, inputRef: inputRef, prefix: _jsx(ButtonFunction, { tabIndex: -1, size: 'xs', className: styles.button, icon: _jsx(MinusSVG, {}), onClick: handleMinusButtonClick, onKeyDown: handleMinusButtonKeyDown, disabled: isMinusButtonDisabled, "data-test-id": 'field-stepper__minus-button' }), postfix: _jsx(ButtonFunction, { tabIndex: -1, size: 'xs', className: styles.button, icon: _jsx(PlusSVG, {}), onClick: handlePlusButtonClick, onKeyDown: handlePlusButtonKeyDown, disabled: isPlusButtonDisabled, "data-test-id": 'field-stepper__plus-button' }), children: _jsxs("div", { className: styles.wrap, children: [_jsx("div", { className: cn({ [styles.prefixWrapper]: prefixSettings.show }), children: prefixSettings.show && prefixSettings.render({ key: prefixSettings.id }) }), _jsx("div", { style: {
                                width: String(value).length * SymbolWidth[size],
                                maxWidth: '100%',
                            }, children: _jsx(InputPrivate, { ref: mergeRefs(ref, inputRef), className: styles.stepper, "data-size": size, value: String(value), tabIndex: 0, onKeyDown: handleInputKeyDown, onChange: handleInputChange, onBlur: handleInputBlur, onFocus: handleInputFocus, disabled: disabled, readonly: readonly, type: 'number', id: id, step: step, name: name, min: min, max: max, "data-test-id": 'field-stepper__input' }) }), _jsx("div", { className: cn({ [styles.postfixWrapper]: postfixSettings.show }), children: postfixSettings.show && postfixSettings.render({ key: postfixSettings.id }) })] }) }) }) })));
});
